import { fetchConfig, debounce } from '@theme/utilities';
import { ThemeEvents, CartAddEvent } from '@theme/events';

class MtoAutoaddProduct extends HTMLElement {
  #busy = false;
  #abort = null;

  // For detecting manual removal (opt-out)
  #lastHadAuto = null;

  connectedCallback() {
    this.#safeEvaluate();

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartSignal);
    document.addEventListener(CartAddEvent.eventName, this.#onCartSignal);
    document.addEventListener('visibilitychange', this.#onVisibilityChange);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartSignal);
    document.removeEventListener(CartAddEvent.eventName, this.#onCartSignal);
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
    this.#abort?.abort();
  }

  #onVisibilityChange = () => {
    if (document.visibilityState === 'visible') this.#safeEvaluate();
  };

  #onCartSignal = () => {
    this.#safeEvaluate();
  };

  #safeEvaluate = debounce(() => this.evaluateAndAct(), 120).bind(this);

  get settings() {
    const enabled = this.dataset.enabled === 'true';
    const thresholdTry = Number(this.dataset.threshold || 0);
    const thresholdCents = Math.round(thresholdTry * 100);

    return {
      enabled,
      thresholdCents,
      basis: this.dataset.basis || 'items_subtotal_price',
      minItems: Number(this.dataset.minItems || 0),
      logic: (this.dataset.logic || 'and').toLowerCase(),
      removeWhenFalse: this.dataset.removeWhenFalse === 'true',
      autoVariantId: Number(this.dataset.autoVariantId || 0),
      autoQty: Math.max(1, Number(this.dataset.autoQuantity || 1)),
      requiredVariantId: Number(this.dataset.requiredVariantId || 0),
    };
  }

  async #fetchCart(signal) {
    const res = await fetch('/cart.js', { signal, headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Failed to fetch /cart.js');
    return await res.json();
  }

  #getSectionsToUpdate() {
    // Horizon updates based on cart-items-component sections
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = new Set();

    cartItemsComponents.forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.add(el.dataset.sectionId);
    });

    return Array.from(sectionIds);
  }

  #cartHasVariant(cart, variantId) {
    if (!variantId || !cart?.items?.length) return false;
    return cart.items.some((it) => Number(it.variant_id) === Number(variantId) && Number(it.quantity) > 0);
  }

  #findLineByVariant(cart, variantId) {
    if (!variantId || !cart?.items?.length) return null;
    const idx = cart.items.findIndex((it) => Number(it.variant_id) === Number(variantId));
    return idx >= 0 ? idx + 1 : null; // 1-based
  }

  #conditionsMet(cart) {
    const s = this.settings;
    const checks = [];

    if (s.thresholdCents > 0) {
      const cartValue = Number(cart?.[s.basis] || 0);
      checks.push(cartValue >= s.thresholdCents);
    }

    if (s.minItems > 0) {
      checks.push(Number(cart?.item_count || 0) >= s.minItems);
    }

    if (s.requiredVariantId > 0) {
      checks.push(this.#cartHasVariant(cart, s.requiredVariantId));
    }

    if (checks.length === 0) return false;
    return s.logic === 'or' ? checks.some(Boolean) : checks.every(Boolean);
  }

  // ---- Customer opt-out (they remove it manually) ----
  #optoutKey(cartToken, variantId) {
    return `mto_autoadd_optout_${cartToken}_${variantId}`;
  }

  #isOptedOut(cart, variantId) {
    const token = cart?.token || 'no_token';
    return sessionStorage.getItem(this.#optoutKey(token, variantId)) === '1';
  }

  #setOptOut(cart, variantId, value) {
    const token = cart?.token || 'no_token';
    const key = this.#optoutKey(token, variantId);
    if (value) sessionStorage.setItem(key, '1');
    else sessionStorage.removeItem(key);
  }

  // Dispatch a cartUpdate event Horizon listens to (no dependency on CartUpdateEvent class)
  #dispatchCartUpdate(cartChangeJson, sections, source = 'mto-autoadd-product') {
    document.dispatchEvent(
      new CustomEvent(ThemeEvents.cartUpdate, {
        bubbles: true,
        detail: {
          resource: cartChangeJson,
          data: {
            source,
            sections,
          },
        },
      })
    );
  }

  async evaluateAndAct() {
    const s = this.settings;
    if (!s.enabled) return;
    if (!s.autoVariantId) return;

    if (this.#busy) return;
    this.#busy = true;

    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    try {
      const cart = await this.#fetchCart(signal);
      const met = this.#conditionsMet(cart);
      const hasAuto = this.#cartHasVariant(cart, s.autoVariantId);

      // Detect manual removal while condition is still true -> opt-out
      if (this.#lastHadAuto === true && hasAuto === false && met === true) {
        this.#setOptOut(cart, s.autoVariantId, true);
      }
      this.#lastHadAuto = hasAuto;

      // If conditions become false, clear opt-out so it can auto-add next time
      if (!met) this.#setOptOut(cart, s.autoVariantId, false);

      const optedOut = this.#isOptedOut(cart, s.autoVariantId);

      // ADD
      if (met && !hasAuto && !optedOut) {
        await this.#addAutoProduct(s.autoVariantId, s.autoQty, signal);
        return;
      }

      // REMOVE WHEN FALSE
      if (!met && s.removeWhenFalse && hasAuto) {
        const line = this.#findLineByVariant(cart, s.autoVariantId);
        if (line) await this.#removeLine(line, signal);
        return;
      }
    } catch (e) {
      console.error('[MTO-Autoadd-Product] error:', e);
    } finally {
      this.#busy = false;
    }
  }

  async #addAutoProduct(variantId, quantity, signal) {
    const sections = this.#getSectionsToUpdate();

    const formData = new FormData();
    formData.append('id', String(variantId));
    formData.append('quantity', String(quantity));
    if (sections.length) formData.append('sections', sections.join(','));

    const cfg = fetchConfig('javascript', { body: formData });

    const res = await fetch(Theme.routes.cart_add_url, {
      ...cfg,
      signal,
      headers: { ...cfg.headers, Accept: 'text/html' },
    });

    const json = await res.json();

    if (json?.status) {
      console.warn('[MTO-Autoadd-Product] add failed:', json);
      return;
    }

    // Let Horizon do its normal add flow UI updates
    document.dispatchEvent(
      new CartAddEvent({}, String(variantId), {
        source: 'mto-autoadd-product',
        itemCount: quantity,
        sections: json.sections,
      })
    );

    // Also broadcast cartUpdate so any listeners update even if they don’t listen to CartAddEvent
    if (json.sections) this.#dispatchCartUpdate(json, json.sections, 'mto-autoadd-product:add');
  }

  async #removeLine(line, signal) {
    const sections = this.#getSectionsToUpdate();

    const body = JSON.stringify({
      line,
      quantity: 0,
      sections: sections.join(','),
      sections_url: window.location.pathname,
    });

    const res = await fetch(`${Theme.routes.cart_change_url}`, {
      ...fetchConfig('json', { body }),
      signal,
    });

    const text = await res.text();
    const json = JSON.parse(text);

    if (json?.errors) {
      console.warn('[MTO-Autoadd-Product] remove failed:', json.errors);
      return;
    }

    // This is the key: broadcast cartUpdate with sections so cart-items-component can morph
    if (json.sections) this.#dispatchCartUpdate(json, json.sections, 'mto-autoadd-product:remove');
  }
}

if (!customElements.get('mto-autoadd-product')) {
  customElements.define('mto-autoadd-product', MtoAutoaddProduct);
}
